import { useRef } from 'react';

/**
 * @typedef {Object} ScrollRefs
 * @property {React.RefObject<HTMLElement>} collectiveRef - Reference to collective section
 * @property {React.RefObject<HTMLElement>} servicesRef - Reference to services section
 * @property {React.RefObject<HTMLElement>} contactRef - Reference to contact section
 */

/**
 * @typedef {Object} ScrollFunctions
 * @property {() => void} scrollToCollective - Function to scroll to collective section
 * @property {() => void} scrollToServices - Function to scroll to services section
 * @property {() => void} scrollToContact - Function to scroll to contact section
 */

/**
 * @typedef {Object} UseScrollToSectionReturn
 * @property {ScrollRefs} refs - Object containing section refs
 * @property {ScrollFunctions} scrollFunctions - Object containing scroll functions
 */

/**
 * Custom hook for managing smooth scrolling to page sections with header offset
 *
 * @param {number} headerOffset - Offset to account for sticky header height
 * @returns {UseScrollToSectionReturn} Object containing refs and scroll functions
 */
export const useScrollToSection = (headerOffset = 84) => {
  const collectiveRef = useRef(null);
  const servicesRef = useRef(null);
  const contactRef = useRef(null);

  /**
   * Scrolls to a referenced element with an offset to account for the sticky header
   *
   * @param {React.RefObject<HTMLElement>} ref - The ref object pointing to the target element
   */
  const scrollWithOffset = (ref) => {
    if (ref.current) {
      const top =
        ref.current.getBoundingClientRect().top +
        window.pageYOffset -
        headerOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  /**
   * Scroll functions for each section
   * @returns {void}
   */
  const scrollToCollective = () => scrollWithOffset(collectiveRef);
  const scrollToServices = () => scrollWithOffset(servicesRef);
  const scrollToContact = () => scrollWithOffset(contactRef);

  return {
    // Refs for sections
    refs: {
      collectiveRef,
      servicesRef,
      contactRef,
    },
    // Scroll functions
    scrollFunctions: {
      scrollToCollective,
      scrollToServices,
      scrollToContact,
    },
  };
};
